import { useEffect, useState } from 'react';
import { teamsApi, teamFoldersApi, workflowsApi } from '@/lib/api';
import { Team, TeamFolder } from '@/types/teams';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, Folder, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { eventEmitter, EVENTS } from '@/lib/events';

interface MoveToTeamFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
  onSuccess: () => void;
}

export function MoveToTeamFolderDialog({
  isOpen,
  onClose,
  workflowId,
  workflowName,
  onSuccess,
}: MoveToTeamFolderDialogProps) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamFolders, setTeamFolders] = useState<TeamFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<TeamFolder | null>(null);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTeams();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTeam) {
      loadTeamFolders(selectedTeam.id);
    }
  }, [selectedTeam]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await teamsApi.list();
      const teamsData = Array.isArray(response.data) 
        ? response.data 
        : response.data?.teams || [];
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamFolders = async (teamId: string) => {
    try {
      setLoading(true);
      const response = await teamFoldersApi.getStructure(teamId);
      const foldersData = response.data || [];
      setTeamFolders(foldersData);
      
      // Auto-select root folder if available
      const rootFolder = foldersData.find((f: TeamFolder) => f.isRoot);
      if (rootFolder) {
        setSelectedFolder(rootFolder);
      }
    } catch (error) {
      console.error('Failed to load team folders:', error);
      toast.error('Failed to load team folders');
    } finally {
      setLoading(false);
    }
  };

  const handleMove = async () => {
    if (!selectedFolder) {
      toast.error('Please select a folder');
      return;
    }

    try {
      setMoving(true);
      await workflowsApi.moveToTeamFolder(workflowId, selectedFolder.id);
      toast.success(`Moved "${workflowName}" to team folder`);

      // Emit event to update sidebar team folder counts
      eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Failed to move workflow:', error);
      // Extract error message from API response (backend returns collection validation errors)
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to move workflow';
      toast.error(errorMessage);
    } finally {
      setMoving(false);
    }
  };

  const handleClose = () => {
    setSelectedTeam(null);
    setTeamFolders([]);
    setSelectedFolder(null);
    onClose();
  };

  // Build tree from flat list by filtering folders with given parentFolderId
  const renderFolderTree = (parentFolderId: string | null = null, level = 0): React.ReactNode[] => {
    // For root level, find the root folder (isRoot = true) or folders with no parent
    const foldersAtLevel = parentFolderId === null
      ? teamFolders.filter(f => f.isRoot)
      : teamFolders.filter(f => f.parentFolderId === parentFolderId && !f.isRoot);

    return foldersAtLevel.map((folder) => {
      // Check if this folder has children
      const hasChildren = teamFolders.some(f => f.parentFolderId === folder.id);

      return (
        <div key={folder.id}>
          <button
            onClick={() => setSelectedFolder(folder)}
            className={cn(
              'w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-2',
              selectedFolder?.id === folder.id && 'bg-accent',
            )}
            style={{ paddingLeft: `${12 + level * 20}px` }}
          >
            <Folder className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1">{folder.name}</span>
            {folder.isRoot && (
              <span className="text-xs text-muted-foreground">(Root)</span>
            )}
            {folder.workflowCount !== undefined && (
              <span className="text-xs text-muted-foreground">
                {folder.workflowCount}
              </span>
            )}
          </button>
          {hasChildren && renderFolderTree(folder.id, level + 1)}
        </div>
      );
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Move to Team Folder</DialogTitle>
          <DialogDescription>
            Select a team and folder to move "{workflowName}" to
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 min-h-[300px]">
          {/* Teams List */}
          <div className="border rounded-lg">
            <div className="p-3 border-b bg-muted/50">
              <h4 className="font-medium text-sm">Select Team</h4>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="p-2 space-y-1">
                {loading && !selectedTeam ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Loading teams...
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No teams available
                  </div>
                ) : (
                  teams.map((team) => (
                    <button
                      key={team.id}
                      onClick={() => setSelectedTeam(team)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-2',
                        selectedTeam?.id === team.id && 'bg-accent',
                      )}
                    >
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1">{team.name}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Folders List */}
          <div className="border rounded-lg">
            <div className="p-3 border-b bg-muted/50">
              <h4 className="font-medium text-sm">Select Folder</h4>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="p-2 space-y-1">
                {!selectedTeam ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Select a team first
                  </div>
                ) : loading ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Loading folders...
                  </div>
                ) : teamFolders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No folders available
                  </div>
                ) : (
                  renderFolderTree()
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={moving}>
            Cancel
          </Button>
          <Button 
            onClick={handleMove} 
            disabled={!selectedFolder || moving}
          >
            {moving ? 'Moving...' : 'Move to Folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
